// MCHD Room Scheduler - Holiday Utilities
// Utility functions for holiday detection and handling
// Created: 08-10-2025 18:38:10

import type { Holiday } from '@mchd/shared';

/**
 * Check if a date is a holiday
 * @param date - Date to check
 * @param holidays - Holiday lookup map (keyed by YYYY-MM-DD string)
 * @returns True if the date is a holiday, false otherwise
 */
export function isDateHoliday(
  date: Date,
  holidays: Map<string, Holiday>
): boolean {
  if (!date || !holidays || holidays.size === 0) {
    return false;
  }

  try {
    // Normalize date to YYYY-MM-DD format (UTC to avoid timezone issues)
    const dateStr = date.toISOString().split('T')[0];
    return holidays.has(dateStr);
  } catch (error) {
    console.error('Error checking holiday:', error);
    return false;
  }
}

/**
 * Get holiday label for a specific date
 * @param date - Date to check
 * @param holidays - Holiday lookup map
 * @returns Holiday label if date is a holiday, null otherwise
 */
export function getHolidayLabel(
  date: Date,
  holidays: Map<string, Holiday>
): string | null {
  if (!date || !holidays || holidays.size === 0) {
    return null;
  }

  try {
    const dateStr = date.toISOString().split('T')[0];
    const holiday = holidays.get(dateStr);
    return holiday?.label || null;
  } catch (error) {
    console.error('Error getting holiday label:', error);
    return null;
  }
}

/**
 * Create holiday lookup map from array
 * @param holidaysArray - Array of holidays
 * @returns Map keyed by date string (YYYY-MM-DD)
 */
export function createHolidayMap(holidaysArray: Holiday[]): Map<string, Holiday> {
  const map = new Map<string, Holiday>();
  
  if (!Array.isArray(holidaysArray)) {
    return map;
  }

  holidaysArray.forEach((holiday) => {
    try {
      // Normalize date to YYYY-MM-DD format
      const dateStr = holiday.dateDay instanceof Date 
        ? holiday.dateDay.toISOString().split('T')[0]
        : holiday.dateDay.toString().split('T')[0];
      
      map.set(dateStr, holiday);
    } catch (error) {
      console.error('Error adding holiday to map:', holiday, error);
    }
  });

  return map;
}

/**
 * Check if multiple dates include any holidays
 * @param dates - Array of dates to check
 * @param holidays - Holiday lookup map
 * @returns Array of dates that are holidays
 */
export function filterHolidayDates(
  dates: Date[],
  holidays: Map<string, Holiday>
): Date[] {
  if (!Array.isArray(dates) || !holidays || holidays.size === 0) {
    return [];
  }

  return dates.filter((date) => isDateHoliday(date, holidays));
}

/**
 * Get all holidays within a date range
 * @param startDate - Start date
 * @param endDate - End date
 * @param holidays - Holiday lookup map
 * @returns Array of holidays within the date range
 */
export function getHolidaysInRange(
  startDate: Date,
  endDate: Date,
  holidays: Map<string, Holiday>
): Holiday[] {
  if (!startDate || !endDate || !holidays || holidays.size === 0) {
    return [];
  }

  const holidaysInRange: Holiday[] = [];
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Iterate through each day in the range
  const currentDate = new Date(start);
  while (currentDate <= end) {
    const dateStr = currentDate.toISOString().split('T')[0];
    const holiday = holidays.get(dateStr);
    
    if (holiday) {
      holidaysInRange.push(holiday);
    }

    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return holidaysInRange;
}

/**
 * Check if date is weekend (Saturday or Sunday)
 * @param date - Date to check
 * @returns True if weekend, false otherwise
 */
export function isWeekend(date: Date): boolean {
  if (!date) return false;
  const day = date.getDay();
  return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
}

/**
 * Check if date is holiday or weekend
 * @param date - Date to check
 * @param holidays - Holiday lookup map
 * @returns True if holiday or weekend, false otherwise
 */
export function isNonWorkingDay(
  date: Date,
  holidays: Map<string, Holiday>
): boolean {
  return isDateHoliday(date, holidays) || isWeekend(date);
}

/**
 * Get next working day (skips holidays and weekends)
 * @param date - Starting date
 * @param holidays - Holiday lookup map
 * @param maxDays - Maximum days to search (default 30)
 * @returns Next working day or null if not found within maxDays
 */
export function getNextWorkingDay(
  date: Date,
  holidays: Map<string, Holiday>,
  maxDays: number = 30
): Date | null {
  const currentDate = new Date(date);
  currentDate.setDate(currentDate.getDate() + 1); // Start from next day

  let daysChecked = 0;
  while (daysChecked < maxDays) {
    if (!isNonWorkingDay(currentDate, holidays)) {
      return currentDate;
    }
    currentDate.setDate(currentDate.getDate() + 1);
    daysChecked++;
  }

  return null; // No working day found within maxDays
}

/**
 * Count working days between two dates
 * @param startDate - Start date
 * @param endDate - End date
 * @param holidays - Holiday lookup map
 * @returns Number of working days
 */
export function countWorkingDays(
  startDate: Date,
  endDate: Date,
  holidays: Map<string, Holiday>
): number {
  if (!startDate || !endDate || startDate > endDate) {
    return 0;
  }

  let workingDays = 0;
  const currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    if (!isNonWorkingDay(currentDate, holidays)) {
      workingDays++;
    }
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return workingDays;
}

